import useSWR from 'swr';
import { agentService, isApiError } from '@dopomogai/api-client';
import type {
  UUID,
  AgentNotificationSettings,
  AgentNotificationSettingsPayload,
} from '@dopomogai/types';
import { AgentKitError } from '../types';

const NOTIFICATION_CACHE_KEY = 'dopomogai/agents/notifications';

const fetchAgentNotificationSettings = async (
  [, id]: [typeof NOTIFICATION_CACHE_KEY, UUID],
): Promise<AgentNotificationSettings> => {
  const res = await agentService.getAgentNotificationSettings(id);
  if (isApiError(res)) {
    throw new AgentKitError(res.message, res);
  }
  return res;
};

export const useAgentNotifications = (agentId: UUID | null) => {
  const cacheKey = agentId ? [NOTIFICATION_CACHE_KEY, agentId] : null;

  const { data, error, isLoading, mutate } = useSWR<AgentNotificationSettings, AgentKitError>(
    cacheKey,
    fetchAgentNotificationSettings,
  );

  const update = async (payload: AgentNotificationSettingsPayload) => {
    if (!agentId) {
      throw new AgentKitError('Agent ID is required.');
    }

    const res = await agentService.updateAgentNotificationSettings(agentId, payload);
    if (isApiError(res)) {
      throw new AgentKitError(res.message, res);
    }

    await mutate(res, false);
    return res;
  };

  return {
    settings: data,
    isLoading,
    error,
    update,
    mutate,
  };
};